import React, { useEffect, useMemo, useState } from 'react'
import SimulatedLayout from '@layouts/SimulatedLayout'
import styled from 'styled-components'
import BottomFixedButtonWrapper from '@components/BottomFixedButtonWrapper'
import usePostMessage from '@hooks/usePostMessage'
import useSamsungInterestEvent from '../../../../services/event/useSamsungInterestEvent'
import { isMobile } from 'react-device-detect'
import useSearchParams from '@hooks/useSearchParams'
import DownloadSheet from '@pages/Event/simulate/v1/EventCompletePage/components/DownloadSheet'
import InstallBottomSheet from '@components/simulated-trading/InstallBottomSheet'
import { useScrollBlock } from '@hooks/useScrollBlock'
import _ from 'lodash'
import SamsungNoticeBox from '@pages/Event/security/samsung/SamsungNoticeBox'
import { Helmet } from 'react-helmet-async'
import { AVAILABLE_SAMSUNG_EVENT_VERSION } from '@constants/index'
import usePostEventParticipated from '../../../../services/event/usePostEventParticipated'
import EventTermsBottomSheet from '@pages/Event/security/samsung/components/EventTermsBottomSheet'
import Lottie from 'lottie-react'
import animationData from '../../../../lotties/loading-gray.json'
import { useHistory } from 'react-router-dom'

const SAMSUNG_EVENT_CODE = 'SAMSUNG_INTEREST'

const Section = styled.section`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
`
const Image = styled.img`
  object-position: top;
  object-fit: contain;
  max-width: 500px;
  width: 100%;
`
const TopImage = styled(Image)`
  aspect-ratio: 3.6/6;
`
const ButtonContainer = styled.div`
  display: flex;
  padding: 16px 24px;
  width: 100%;
  max-width: 500px;
`
const BottomButton = styled.button`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  height: 56px;
  background-color: #0e6dfb;
  width: 100%;
  border-radius: 12px;
  font-family: Pretendard400, sans-serif;
  font-size: 16px;
  color: white;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    color: #999ba9;
    background-color: #dcdeeb;
  }
`

const FROM_WEB_TEXT = '앱 설치하고 매달 5% 받기'

const InterestEarnedEvent = (mode: 'staging' | 'production' = 'production') => {
  const { getParam } = useSearchParams()
  const { postMessageHandler } = usePostMessage()
  const history = useHistory()
  const searchParams = useSearchParams()
  const isOpenBottomSheet = searchParams.getParam('bottomSheet') === 'true'
  const {
    getSamsungInterestEvent: { data, isLoading },
  } = useSamsungInterestEvent(mode)
  const { mutate } = usePostEventParticipated(mode)
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)

  const [blockScroll, allowScroll] = useScrollBlock()
  const AppVersion = localStorage.getItem('version')
  const isMobileDevice = isMobile
  const userId = getParam('userId')

  const onClickStoreBottomSheet = () => {
    setIsBottomSheetOpen(!isBottomSheetOpen)
  }

  const onClickBottomSheetOpen = () => {
    const {
      location: { pathname, search },
    } = history
    history.push(pathname + search + '&bottomSheet=true')
  }
  const tagPushHandler = () => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'event_samsung_01_detail_btn_click',
      user_id: userId,
      samsung_interest_event_status: data?.alreadyParticipate
        ? 'ALREADY'
        : 'FROM_WEB',
    })
  }

  const distributeWithHasAccount = () => {
    if (!data) return
    if (data.existsSamsungAccount) {
      //삼성 계좌 있는 경우
      postMessageHandler({
        action: 'toMyTab',
      })
    } else {
      // 삼성 계좌 없는 경우
      postMessageHandler({
        action:
          Number(AppVersion) >= AVAILABLE_SAMSUNG_EVENT_VERSION
            ? 'toCreateSamsungContract'
            : 'toMyTab',
      })
    }
  }
  // 제 3자 동의 후 이벤트 참여하기 버튼 클릭
  const onClickEventParticipated = _.debounce(() => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'event_samsung_01_detail_btn_click',
      user_id: userId,
      samsung_interest_event_status: 'TARGET',
    })
    mutate(
      {
        userId,
        eventCode: SAMSUNG_EVENT_CODE,
      },
      {
        onSuccess: () => {
          if (data) {
            distributeWithHasAccount()
          }
        },
        onSettled: () => {
          history.goBack()
        },
      }
    )
  }, 300)

  const onClick = _.debounce(() => {
    // WEB 에서 접근 한 경우
    if (!userId || !data) {
      onClickStoreBottomSheet()
      tagPushHandler()

      return
    }
    if (!data) return
    if (data.alreadyParticipate) {
      tagPushHandler()
      // 이미 이벤트 참여 한 경우
      distributeWithHasAccount()
    } else {
      // 이벤트 참여 이력이 없는 경우
      onClickBottomSheetOpen()
    }
  }, 300)

  const isDisabledButton = data && !data.isTarget

  const buttonText = useMemo(() => {
    if (isLoading) return
    else if (!data || !userId) {
      return FROM_WEB_TEXT
    } else if (data.isTarget) {
      if (data.alreadyParticipate) {
        return '이자 받으러 가기'
      } else {
        return '신청하고 이자 받으러 가기'
      }
    } else {
      return '대상 고객이 아닙니다'
    }
  }, [data, isLoading])

  useEffect(() => {
    if (isBottomSheetOpen || isOpenBottomSheet) {
      blockScroll()
    } else {
      allowScroll()
    }
    return () => allowScroll()
  }, [isBottomSheetOpen, isOpenBottomSheet])

  return (
    <>
      <div>
        <Helmet>
          <title>연5% 이자받고 공모주 시작하기</title>
          <meta
            name={'description'}
            content={
              '공모주 청약을 해야하는지 말아야 하는지, 어느 증권사에 청약하는 게 유리한지, 콕 집어서 답을 알려드려요.'
            }
          />
          <meta property='og:type' content='website' />
          <meta property='og:title' content='연5% 이자받고 공모주 시작하기' />
          <meta
            property='og:description'
            content='공모주 청약을 해야하는지 말아야 하는지, 어느 증권사에 청약하는 게 유리한지, 콕 집어서 답을 알려드려요.'
          />
          <meta
            property='og:image'
            content='https://160ipo.com/samsung-event-og.png'
          />
          <meta property='og:image:width' content='800' />
          <meta property='og:image:height' content='400' />
        </Helmet>
      </div>
      {isOpenBottomSheet && (
        <EventTermsBottomSheet
          onClickEventParticipated={onClickEventParticipated}
          isOpen={isOpenBottomSheet}
          handleClose={() => history.goBack()}
        />
      )}
      {isBottomSheetOpen && (
        <>
          {isMobileDevice ? (
            <DownloadSheet
              isOpen={isBottomSheetOpen}
              handleClose={onClickStoreBottomSheet}
            />
          ) : (
            <InstallBottomSheet onClose={onClickStoreBottomSheet} />
          )}
        </>
      )}
      <SimulatedLayout hasClose={false} isFirst={true}>
        <Section>
          <TopImage
            src={'/images/event/samsung-interest/samsung-event-1.png'}
          />
          <Image src={'/images/event/samsung-interest/samsung-event-2.png'} />
          <Image src={'/images/event/samsung-interest/samsung-event-3.png'} />
          <Image src={'/images/event/samsung-interest/samsung-event-4.png'} />
          <Image src={'/images/event/samsung-interest/samsung-event-5.png'} />
          <SamsungNoticeBox />
        </Section>
        <BottomFixedButtonWrapper>
          <ButtonContainer>
            <BottomButton disabled={isDisabledButton} onClick={onClick}>
              {isLoading ? (
                <Spinner>
                  <Lottie animationData={animationData} />
                </Spinner>
              ) : (
                buttonText
              )}
            </BottomButton>
          </ButtonContainer>
        </BottomFixedButtonWrapper>
      </SimulatedLayout>
    </>
  )
}

const Spinner = styled.div`
  position: absolute;
  height: 100px;
  width: 100px;
  top: 4px;
`

export default InterestEarnedEvent
